import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Paper,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  ButtonBase,
  Popover,
  Typography,
} from '@mui/material';
import ViewQuiltRoundedIcon from '@mui/icons-material/ViewQuiltRounded';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AssessmentIcon from '@mui/icons-material/Assessment';

import JwtValidator from '../../utils/JwtValidator';
import { AccountSolution } from '@thingslog/repositories';
import { alarmsQueryClient } from '../../utils/ReactQueryClients';
import { Delete, Event, LocalShipping, Inventory, Inbox } from '@mui/icons-material';
import { cn } from '@thingslog/ui-components';
import { useCurrentCompanyId, useViewport } from '../../hooks';

interface NavbarMenuItemBase {
  id: string;
  type: 'link' | 'submenu';
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  badge?: number;
  solutions?: AccountSolution[];
}

interface NavbarMenuItemLink extends NavbarMenuItemBase {
  type: 'link';
  redirectTo: string;
}

interface NavbarMenuItemWithSubmenu extends NavbarMenuItemBase {
  type: 'submenu';
  submenuItems: NavbarMenuItemLink[];
}

type NavbarMenuItem = NavbarMenuItemLink | NavbarMenuItemWithSubmenu;

interface NavbarProps {
  variant: 'horizontal' | 'vertical';
}

function Navbar({ variant }: NavbarProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const jwtValidator = useMemo(() => new JwtValidator(), []);
  const { useNumberOfActiveAlarms } = useMemo(() => alarmsQueryClient, []);
  const [popperAnchorEl, setPopperAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const open = Boolean(popperAnchorEl);
  const id = open ? 'popover-menu' : undefined;
  const isMobile = useViewport('sm');
  const companyId = useCurrentCompanyId();

  const clearCloseTimeout = (): void => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (isMobile) {
      return;
    }

    clearCloseTimeout();
    setPopperAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = (): void => {
    if (isMobile) {
      return;
    }

    closeTimeoutRef.current = setTimeout(() => {
      setPopperAnchorEl(null);
    }, 100); // Small delay to allow moving to popover
  };

  const handlePopoverMouseEnter = (): void => {
    if (isMobile) {
      return;
    }

    clearCloseTimeout();
  };

  const handlePopoverMouseLeave = (): void => {
    if (isMobile) {
      return;
    }

    setPopperAnchorEl(null);
  };

  const handleButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: NavbarMenuItem
  ): void => {
    if (item.type === 'link') {
      navigate(item.redirectTo);
      return;
    }

    if (!isMobile) {
      return;
    }

    setPopperAnchorEl((currentAnchorEl: HTMLButtonElement | null): HTMLButtonElement | null =>
      currentAnchorEl === event.currentTarget ? null : event.currentTarget
    );
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const currentPath = location.pathname;

  /* ---- data ---- */

  const { data: alarmsCount } = useNumberOfActiveAlarms(companyId ?? 0, undefined, [7, 8, 9], {
    enabled:
      !jwtValidator.hasRole('ROLE_PUBLIC') &&
      jwtValidator.decodedToken !== null &&
      companyId !== undefined,
  });

  if (jwtValidator.hasRole('ROLE_RESTRICTED')) {
    return <></>;
  }

  const isSuperAdmin = jwtValidator.hasRole('ROLE_SUPER_ADMIN');

  const shouldShowPage = (item: NavbarMenuItem): boolean => {
    if (isSuperAdmin) {
      return true;
    }

    if (!item.solutions || item.solutions.length === 0) {
      return true;
    }

    return item.solutions.some((solution: AccountSolution) => jwtValidator.isSolution(solution));
  };

  /* ---- render ---- */

  const menuItems: NavbarMenuItem[] = useMemo(() => {
    return [
      {
        id: 'home',
        type: 'link',
        redirectTo: '/home',
        icon: <ViewQuiltRoundedIcon fontSize="medium" />,
        label: t('menu_home'),
        isActive: currentPath === '/home',
      },
      {
        id: 'alarms',
        type: 'link',
        redirectTo: '/alarms',
        icon: <NotificationImportantIcon fontSize="medium" />,
        label: t('menu_alarms'),
        isActive: currentPath.startsWith('/alarms'),
        badge: alarmsCount,
      },
      {
        id: 'task-scheduler',
        type: 'link',
        redirectTo: '/taskScheduler',
        icon: <LightbulbIcon fontSize="medium" />,
        label: t('task_scheduler_link_label'),
        isActive: currentPath.startsWith('/taskScheduler'),
        solutions: [AccountSolution.STREET_LIGHTS, AccountSolution.PUMP_STATION],
      },
      {
        id: 'reports',
        type: 'submenu',
        icon: <AssessmentIcon fontSize="medium" />,
        label: t('menu_reports', 'Reports'),
        solutions: [AccountSolution.SMART_WASTE],
        isActive:
          currentPath.startsWith('/reports') ||
          currentPath.startsWith('/wastebags') ||
          currentPath.startsWith('/collections') ||
          currentPath.startsWith('/truck-collections') ||
          currentPath.startsWith('/truck-collections-by-container') ||
          currentPath.startsWith('/container-collections'),
        submenuItems: [
          {
            id: 'trashbags',
            type: 'link',
            redirectTo: '/wastebags',
            icon: <Delete sx={{ mr: 1.5, fontSize: 20 }} />,
            label: t('menu_trashbags', 'Waste bags'),
            isActive: currentPath === '/wastebags',
          },
          {
            id: 'collections',
            type: 'link',
            redirectTo: '/collections',
            icon: <Event sx={{ mr: 1.5, fontSize: 20 }} />,
            label: t('menu_collections', 'Collections'),
            isActive: currentPath === '/collections',
          },
          {
            id: 'truck-collections',
            type: 'link',
            redirectTo: '/truck-collections',
            icon: <LocalShipping sx={{ mr: 1.5, fontSize: 20 }} />,
            label: t('menu_truck_collections', 'Truck Collections'),
            isActive: currentPath === '/truck-collections',
          },
          {
            id: 'truck-collections-by-container',
            type: 'link',
            redirectTo: '/truck-collections-by-container',
            icon: <Inventory sx={{ mr: 1.5, fontSize: 20 }} />,
            label: t('menu_truck_collections_by_container', 'Truck Collections by Container'),
            isActive: currentPath === '/truck-collections-by-container',
          },
          {
            id: 'container-collections',
            type: 'link',
            redirectTo: '/container-collections',
            icon: <Inbox sx={{ mr: 1.5, fontSize: 20 }} />,
            label: t('menu_container_collections', 'Container Collections'),
            isActive: currentPath === '/container-collections',
          },
        ],
      },
    ];
  }, [currentPath, isMobile, t]);

  return (
    <nav
      className={cn(
        'flex items-center justify-center bg-portal-charcoal text-white',
        variant === 'horizontal' ? 'h-12 flex-row gap-1 px-1' : 'w-[4.75rem] flex-col gap-1 py-2'
      )}
    >
      {menuItems.map((item: NavbarMenuItem) => {
        if (!shouldShowPage(item)) {
          return null;
        }

        return (
          <div key={item.id} className={cn('w-full', variant === 'horizontal' ? 'py-1' : 'px-1')}>
            <button
              aria-describedby={id}
              className={cn(
                'relative flex w-full cursor-pointer flex-col items-center justify-center rounded-md border-none shadow-none',
                variant === 'horizontal'
                  ? 'min-h-[2.5rem] px-2 py-1'
                  : 'min-h-[3.25rem] px-0.5 py-1',
                'bg-portal-charcoal hover:bg-portal-charcoal-light transition-colors',
                item.isActive ? 'bg-portal-charcoal-light text-white' : 'text-white/70'
              )}
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void =>
                handleButtonClick(event, item)
              }
              onMouseEnter={item.type === 'submenu' ? handleMouseEnter : undefined}
              onMouseLeave={item.type === 'submenu' ? handleMouseLeave : undefined}
            >
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-portal-seagreen px-1 text-[0.6875rem] font-medium text-white">
                  {item.badge}
                </span>
              )}
              {item.icon}
              {!isMobile && (
                <Typography
                  variant="caption"
                  className="mt-0.5 max-w-full truncate text-[0.6875rem] leading-tight"
                >
                  {item.label}
                </Typography>
              )}
            </button>
            {item.type === 'submenu' && (
              <Popover
                id={id}
                open={open}
                anchorEl={popperAnchorEl}
                onClose={(): void => setPopperAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                className=""
                disableRestoreFocus
                onMouseEnter={handlePopoverMouseEnter}
                onMouseLeave={handlePopoverMouseLeave}
              >
                <Paper className="min-w-[13rem]">
                  <MenuList>
                    {(item as NavbarMenuItemWithSubmenu).submenuItems.map(
                      (submenuItem: NavbarMenuItemLink) => {
                        return (
                          <MenuItem
                            key={submenuItem.id}
                            selected={currentPath === submenuItem.redirectTo}
                            onClick={(): void => {
                              navigate(submenuItem.redirectTo);
                              setPopperAnchorEl(null);
                            }}
                          >
                            {submenuItem.icon}
                            <span className="ml-2">{submenuItem.label}</span>
                          </MenuItem>
                        );
                      }
                    )}
                  </MenuList>
                </Paper>
              </Popover>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export default Navbar;
